<script lang="ts">
  import { onMount } from 'svelte';

  import Overlay from './partials/Overlay.svelte';
  import { OverlayType, type HomepageClippingHeroProps } from './types';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';

  type $$Props = HomepageClippingHeroProps;

  export let entryId: HomepageClippingHeroProps['entryId'];
  export let text: HomepageClippingHeroProps['text'];
  export let buttonLinks: HomepageClippingHeroProps['buttonLinks'];
  export let videoLink: HomepageClippingHeroProps['videoLink'];
  export let video: HomepageClippingHeroProps['video'];
  export let options: HomepageClippingHeroProps['options'];
  export let customCssClass: HomepageClippingHeroProps['customCssClass'];

  let videos: NodeListOf<HTMLVideoElement> | null;

  const pauseVideos = () => {
    if (videos) {
      for (const video of videos) {
        video.pause();
      }
    }
  };

  onMount(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const reduceMotion = mediaQuery.matches;
    videos = document.querySelectorAll('.HomepageClippingHero__video > video');
    if (reduceMotion) {
      pauseVideos();
    }
  });
</script>

<div
  class="HomepageClippingHero HomepageClippingHero-{entryId}-box-properties {customCssClass}"
  class:short-variant={options.shortVariant}
  data-entry-id={entryId}
  style:--overlayBackground={options.overlayBackground ?? 'transparent'}
  style:--navHeight="69px"
  style:margin-bottom={options.marginBottom}
>
  <div class="homepage">
    <div class="hero">
      <div class="video-container">
        <DynamicChildrenComponent
          children={video}
          passThroughProps={{
            customCssClass: 'HomepageClippingHero__video',
          }}
        />
        <Overlay {text} {buttonLinks} {videoLink} type={OverlayType.Before} />
      </div>
      {#if !options.shortVariant}
        <div class="reveal-container">
          <Overlay {text} {buttonLinks} {videoLink} type={OverlayType.Reveal} />
        </div>
      {/if}
    </div>
  </div>
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/core/config/_media_queries' as mediaQueries;

  @layer custom-components {
    // PWDS Monochrome test
    :global(.BasePage[data-design-system='Public Web Design System'] .HomepageClippingHero.sq-custom-pwds-monochrome) {
      --action-variant-button-alt-border-color: white !important;
      --action-variant-button-alt-background-color: white !important;
      --action-variant-video-text-color: white !important;
    }

    .HomepageClippingHero {
      position: relative;
      z-index: 1;

      --heroVideoHeight: clamp(540px, 100svh, 1200px);
      &.short-variant {
        --heroVideoHeight: clamp(380px, 82svh, 860px);
      }
    }

    .homepage {
      display: inline-block;
      width: 100%;
    }

    .video-container {
      align-items: center;
      border-radius: 15px;
      clip-path: inset(0 0 0 0);
      display: flex;
      height: calc(var(--heroVideoHeight) - var(--navHeight) - 40px);

      justify-content: center;
      overflow: clip;
      position: relative;
      z-index: 1;

      .overlay {
        z-index: 0;
      }

      @include mediaQueries.until('medium') {
        @media (min-aspect-ratio: 8/5) {
          height: calc(var(--heroVideoHeight) - var(--navHeight) - 20px);
        }
      }

      @include mediaQueries.until('small') {
        @media (min-aspect-ratio: 15/32) {
          height: calc(var(--heroVideoHeight) - var(--navHeight) - 80px);
        }
      }
      :global {
        .AproposVideo {
          position: relative;
          height: 100%;
          width: 100%;

          &::after {
            content: '';
            position: absolute;
            inset: 0;
            background: var(--overlayBackground);
          }
        }

        .VideoComponent {
          position: relative;
          height: 100%;
          width: 100%;

          &::after {
            content: '';
            position: absolute;
            inset: 0;
            background: var(--overlayBackground);
          }
        }
        video {
          border-radius: 15px;
          height: 100%;
          object-fit: cover;
          width: 100%;
        }
      }
    }

    .hero {
      --textColor: white;
      position: relative;

      padding: 0 clamp(16px, 5vw, 40px);
      width: 100%;
    }
  }
</style>
